import { motion } from "framer-motion";
import { Plus, Tag, Truck, Boxes } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { fmtEuro } from "@/lib/api";
import { ZoomableImage } from "@/components/ZoomableImage";

const Products = ({ products }) => {
  const { t, lang } = useLang();
  const { addItem } = useCart();

  return (
    <section id="products" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="mb-14 max-w-2xl">
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-heading text-4xl font-bold tracking-tight text-primary lg:text-5xl">
          {t.products.title}
        </motion.h2>
        <p className="mt-4 text-lg text-muted-foreground">{t.products.sub}</p>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">{t.products.note}</p>
      </div>

      <div className="mb-10 flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-3 rounded-2xl border border-accent/30 bg-accent/10 px-5 py-4"
          data-testid="products-volume-note">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-white">
            <Tag className="h-5 w-5" />
          </span>
          <p className="text-base font-medium text-secondary">{t.products.volumeNote}</p>
        </div>
        <div className="flex items-center gap-3 rounded-2xl bg-secondary px-6 py-4 shadow-[0_16px_40px_-16px_rgba(10,90,60,0.7)] ring-1 ring-inset ring-white/10"
          data-testid="products-shipping-note">
          <span className="grid h-10 w-10 shrink-0 animate-pulse place-items-center rounded-full bg-white/20 text-white">
            <Truck className="h-5 w-5" />
          </span>
          <p className="text-base font-extrabold uppercase tracking-wide text-white sm:text-lg">{t.products.shippingNote}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {products.map((p, i) => {
          const info = t.products.items[p.id];
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.1 }}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow hover:shadow-[0_24px_50px_-20px_rgba(10,37,64,0.25)]"
              data-testid={`product-card-${p.id}`}>
              <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                <ZoomableImage src={p.image} alt={info.name}
                  testId={`product-image-${p.id}`}
                  className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />
                {p.badge && (
                  <span className="absolute left-3 top-3 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-white">
                    {p.badge[lang] || p.badge.nl}
                  </span>
                )}
                {p.original_price && (
                  <span data-testid={`original-price-${p.id}`}
                    className="absolute right-3 top-3 rounded-full bg-primary px-3 py-1 text-sm font-bold text-white/70 line-through decoration-accent decoration-2">
                    {fmtEuro(p.original_price)}
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-heading text-lg font-semibold leading-tight text-primary">{info.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{info.desc}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="flex items-baseline gap-2 font-heading">
                    <span className="text-xl font-bold text-secondary">{fmtEuro(p.price)}</span>
                    {p.original_price && (
                      <span className="text-sm font-medium text-muted-foreground line-through">{fmtEuro(p.original_price)}</span>
                    )}
                  </span>
                  <button onClick={() => addItem(p)} data-testid={`add-${p.id}`}
                    className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:bg-accent active:scale-95">
                    <Plus className="h-4 w-4" /> {t.products.add}
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <a href="#contact" data-testid="products-bulk-note"
        className="mt-8 flex items-center justify-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-6 py-4 text-center transition-colors hover:bg-primary/10">
        <Boxes className="h-5 w-5 shrink-0 text-accent" />
        <p className="text-base font-medium text-primary">{t.products.bulkNote}</p>
      </a>
    </section>
  );
};

export default Products;
